import React, { useMemo, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import CardTable from "components/CardTable/CardTable";
import { TableInputs } from "components/CardTable/TableInputs";
import { useToggle } from "hooks/useToggle";
import { useOpenComponent } from "hooks/useOpenComponent";
import AddIcon from "@mui/icons-material/Add";
import { columns } from "./columns";

import RegularButton from "components/CustomButtons/Button";
import { Home } from "@mui/icons-material";
import { useSearch } from "hooks/useSearch";
import { ModalV2 } from "components/Modal/ModalV2";

import { FACILITIES_PHRASE } from "./config";
import { FacilitiesForm } from "./FacilitiesForm";
import { AssignDiscountForm } from "./AssignDiscountForm";

import {
  fetchFacilities,
  newFacility,
  clearFacilities,
  handleFacilitiesInputsAction,
} from "redux/actions/buildings/facilities/facilitiesActions";
import { fetchCollectPeriods } from "redux/actions/buildings/collectPeriods/collectPeriodsActions";
import { fetchRoutes } from "redux/actions/buildings/routes/routesActions";

const LOADINGS = [
  "fetchFacilities",
  "fetchNewFacility",
  "fetchEditFacility",
];

export const FacilitiesPage = () => {
  const dispatch = useDispatch();
  const { isOpen, handleIsOpen } = useOpenComponent();
  const [isDiscountOpen, handleOpenDiscount] = useToggle();
  const { setSnackbar, clearSearch, buildCompLoadings } = useSearch();
  const { isLoadingComp } = useSelector(state => state.search);
  const { facilitiesMap, station } = useSelector(state => state.facilities);
  const { collectPeriods } = useSelector(state => state.collectPeriods);
  const { routes } = useSelector(state => state.routesBuildings);

  const facilitiesMemo = useMemo(
    () =>
      Object.keys(facilitiesMap)
        ?.map(id => facilitiesMap[id])
        .map(facility => ({
          ...facility,
          route: routes.find(_ => _.id == facility.idRoute)?.name,
          collectPeriod: collectPeriods.find(
            _ => _.id == facility.idCollectPeriod
          )?.name,
          modal: handleIsOpen,
          discountModal: handleOpenDiscount
        })),
    [facilitiesMap, routes, collectPeriods]
  );

  const getFacilities = useCallback(
    stationID => {
      dispatch(fetchFacilities(stationID));
    },
    [dispatch, fetchFacilities]
  );

  const getRoutes = useCallback(
    stationID => {
      dispatch(fetchRoutes(stationID));
    },
    [dispatch]
  );

  const getCollectPeriods = useCallback(
    stationID => {
      dispatch(fetchCollectPeriods(stationID));
    },
    [dispatch]
  );

  const handleSubmit = useCallback(() => {
    if (station.length < 1) {
      setSnackbar(FACILITIES_PHRASE.search.messages.noStations);
      return;
    }
    const stationID = station.id;
    getFacilities(stationID);
    getRoutes(stationID);
    getCollectPeriods(stationID);
  }, [station, setSnackbar]);

  const submitMemoized = useCallback((e) => {
    e.preventDefault();
    handleSubmit();
  }, [handleSubmit]);

  const handleClose = () => {
    handleIsOpen();
  };

  const handleOpenAdd = () => {
    dispatch(newFacility());
    handleIsOpen();
  };

  useEffect(() => {
    buildCompLoadings(LOADINGS);
  }, []);

  useEffect(() => {
    clearSearch();
    dispatch(clearFacilities());
  }, [clearSearch]);

  return (
    <React.Fragment>
      <CardTable
        data={facilitiesMemo}
        columns={columns}
        title={FACILITIES_PHRASE.page.table.title}
        loading={isLoadingComp.fetchFacilities}
        icon={<Home />}
        hasHeader
        blockBtn
        hasControl
        controls={
          true && (
            <RegularButton
              round
              justIcon
              color="info"
              style={{ margin: 10 }}
              onClick={handleOpenAdd}
              size="sm"
            >
              <AddIcon style={{ margin: 0 }} />
            </RegularButton>
          )
        }
        tableContent={
          <TableInputs
            reducer="facilities"
            stations
            dispatchAction={handleFacilitiesInputsAction}
            submitAction={submitMemoized}
          />
        }
      />
      <ModalV2 open={isOpen} onClose={handleClose} toRight>
        {
          <FacilitiesForm
            handleClose={handleClose}
            stationSelected={station}
          />
        }
      </ModalV2>
      <ModalV2 open={isDiscountOpen} onClose={() => handleOpenDiscount()} toRight>
        {<AssignDiscountForm handleClose={() => handleOpenDiscount()} stationSelected={station} />}
      </ModalV2>
    </React.Fragment>
  );
};
